// ═══════════════════════════════════════════════════════════════════════════════
// Admin — invitation codes CRUD + usage log
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DEFAULT_TIMEZONE,
  INVITATION_CODE_LENGTH,
  INVITATION_CODE_STATUS,
  INVITATION_CODE_TYPES,
  type AdminUserListItem,
  type AdminUserListResponse,
  type InvitationCode,
  type InvitationCodeListResponse,
  type InvitationCodeType,
  type InvitationCodeUsage,
  type InvitationCodeUsageListResponse,
} from '@fdh/shared';
import { adminApi } from '../../lib/adminApi';
import { adminPortalPath } from '../../lib/adminPortalPaths';
import { useLanguage } from '../../contexts/LanguageContext';

type AdminTab = 'codes' | 'usages';

type CodeFormState = {
  code: string;
  autoGenerate: boolean;
  codeType: InvitationCodeType;
  level: number;
  levelDurationDays: string;
  remark: string;
  timezone: string;
  startDate: string;
  endDate: string;
  maxUses: string;
  unlimitedMaxUses: boolean;
  immediateStart: boolean;
  permanentEnd: boolean;
  status: typeof INVITATION_CODE_STATUS.ACTIVE | typeof INVITATION_CODE_STATUS.DISABLED;
  referrerUserId: string | null;
  referrerEmail: string;
  referrerNickname: string;
  referrerRole: string;
};

const emptyForm: CodeFormState = {
  code: '',
  autoGenerate: true,
  codeType: INVITATION_CODE_TYPES.REGISTRATION_GRANT,
  level: 1,
  levelDurationDays: '30',
  remark: '',
  timezone: DEFAULT_TIMEZONE,
  startDate: '',
  endDate: '',
  maxUses: '100',
  unlimitedMaxUses: false,
  immediateStart: true,
  permanentEnd: true,
  status: INVITATION_CODE_STATUS.ACTIVE,
  referrerUserId: null,
  referrerEmail: '',
  referrerNickname: '',
  referrerRole: '',
};

const resolveLevelForCodeType = (codeType: InvitationCodeType): number => {
  if (codeType === INVITATION_CODE_TYPES.L1_TRIAL_EXTEND) {
    return 1;
  }
  if (codeType === INVITATION_CODE_TYPES.L3_TRIAL_PERIOD) {
    return 3;
  }
  return 1;
};

const toFormFromCode = (item: InvitationCode): CodeFormState => ({
  code: item.code,
  autoGenerate: false,
  codeType: item.codeType,
  level: item.level,
  levelDurationDays: item.levelDurationDays != null ? String(item.levelDurationDays) : '30',
  remark: item.remark ?? '',
  timezone: item.timezone,
  startDate: item.startDate ?? '',
  endDate: item.endDate ?? '',
  maxUses: item.maxUses != null ? String(item.maxUses) : '100',
  unlimitedMaxUses: item.maxUses == null,
  immediateStart: item.startDate == null,
  permanentEnd: item.endDate == null,
  status: item.status,
  referrerUserId: item.referrerUserId,
  referrerEmail: item.referrer?.email ?? '',
  referrerNickname: item.referrer?.nickname ?? '',
  referrerRole: item.referrer?.role ?? '',
});

export const AdminInvitationCodesPage = () => {
  const { t } = useLanguage();
  const [tab, setTab] = useState<AdminTab>('codes');
  const [codes, setCodes] = useState<InvitationCode[]>([]);
  const [usages, setUsages] = useState<InvitationCodeUsage[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CodeFormState>(emptyForm);
  const [codeFilter, setCodeFilter] = useState('');
  const [remarkFilter, setRemarkFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'disabled'>('all');
  const [usageCodeFilter, setUsageCodeFilter] = useState('');
  const [usageStartDate, setUsageStartDate] = useState('');
  const [usageEndDate, setUsageEndDate] = useState('');
  const [referrerSearchEmail, setReferrerSearchEmail] = useState('');
  const [referrerSearchResults, setReferrerSearchResults] = useState<AdminUserListItem[]>([]);
  const [referrerSearching, setReferrerSearching] = useState(false);

  const loadCodes = useCallback(async () => {
    setError('');
    try {
      const params = new URLSearchParams();
      if (codeFilter.trim()) {
        params.set('code', codeFilter.trim());
      }
      if (remarkFilter.trim()) {
        params.set('remark', remarkFilter.trim());
      }
      if (statusFilter !== 'all') {
        params.set('status', statusFilter);
      }
      const res = await adminApi.get<InvitationCodeListResponse>(
        `/admin/invitation-codes?${params.toString()}`,
      );
      if (res.success && res.data?.codes) {
        setCodes(res.data.codes);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
    }
  }, [codeFilter, remarkFilter, statusFilter, t]);

  const loadUsages = useCallback(async () => {
    setError('');
    try {
      const params = new URLSearchParams();
      if (usageCodeFilter.trim()) {
        params.set('code', usageCodeFilter.trim());
      }
      if (usageStartDate) {
        params.set('startDate', usageStartDate);
      }
      if (usageEndDate) {
        params.set('endDate', usageEndDate);
      }
      const res = await adminApi.get<InvitationCodeUsageListResponse>(
        `/admin/invitation-code-usages?${params.toString()}`,
      );
      if (res.success && res.data?.usages) {
        setUsages(res.data.usages);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
    }
  }, [usageCodeFilter, usageStartDate, usageEndDate, t]);

  useEffect(() => {
    if (tab === 'codes') {
      void loadCodes();
    } else {
      void loadUsages();
    }
  }, [tab, loadCodes, loadUsages]);

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        code: form.autoGenerate ? undefined : form.code.trim().toUpperCase(),
        autoGenerate: form.autoGenerate,
        codeType: form.codeType,
        level: resolveLevelForCodeType(form.codeType),
        levelDurationDays:
          form.codeType === INVITATION_CODE_TYPES.REGISTRATION_GRANT
            ? null
            : Number(form.levelDurationDays) || null,
        remark: form.remark.trim() || null,
        timezone: form.timezone,
        startDate: form.immediateStart ? null : form.startDate || null,
        endDate: form.permanentEnd ? null : form.endDate || null,
        maxUses: form.unlimitedMaxUses ? null : Number(form.maxUses),
        referrerUserId: form.referrerUserId,
      };
      if (editingId) {
        await adminApi.put<InvitationCode>(`/admin/invitation-codes/${editingId}`, {
          ...payload,
          code: form.autoGenerate ? undefined : form.code.trim().toUpperCase(),
          status: form.status,
        });
      } else {
        await adminApi.post<InvitationCode>('/admin/invitation-codes', payload);
      }
      setEditingId(null);
      setForm(emptyForm);
      setReferrerSearchEmail('');
      setReferrerSearchResults([]);
      await loadCodes();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: InvitationCode) => {
    setEditingId(item.id);
    setForm(toFormFromCode(item));
    setReferrerSearchEmail(item.referrer?.email ?? '');
    setReferrerSearchResults([]);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
    setReferrerSearchEmail('');
    setReferrerSearchResults([]);
    setError('');
  };

  const handleSearchReferrer = async () => {
    const email = referrerSearchEmail.trim();
    if (!email) {
      setReferrerSearchResults([]);
      return;
    }
    setReferrerSearching(true);
    setError('');
    try {
      const params = new URLSearchParams({ email, pageSize: '10' });
      const res = await adminApi.get<AdminUserListResponse>(`/admin/users?${params.toString()}`);
      if (res.success && res.data?.users) {
        setReferrerSearchResults(res.data.users);
      } else {
        setReferrerSearchResults([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
      setReferrerSearchResults([]);
    } finally {
      setReferrerSearching(false);
    }
  };

  const handleSelectReferrer = (user: AdminUserListItem) => {
    setForm((prev) => ({
      ...prev,
      referrerUserId: user.id,
      referrerEmail: user.email,
      referrerNickname: user.nickname,
      referrerRole: user.role,
    }));
    setReferrerSearchEmail(user.email);
    setReferrerSearchResults([]);
  };

  const handleClearReferrer = () => {
    setForm((prev) => ({
      ...prev,
      referrerUserId: null,
      referrerEmail: '',
      referrerNickname: '',
      referrerRole: '',
    }));
    setReferrerSearchEmail('');
    setReferrerSearchResults([]);
  };

  const tabClass = (value: AdminTab): string =>
    [
      'rounded-md px-4 py-2 text-sm font-medium transition-colors',
      tab === value ? 'bg-amber-500 text-slate-950' : 'bg-slate-800 text-slate-300 hover:bg-slate-700',
    ].join(' ');

  return (
    <div>
      <h1 className="text-2xl font-semibold text-white">{t('admin.invitationCodesTitle')}</h1>
      <p className="mt-1 text-sm text-slate-400">{t('admin.invitationCodesSubtitle')}</p>

      <div className="mt-6 flex gap-2">
        <button type="button" className={tabClass('codes')} onClick={() => setTab('codes')}>
          {t('admin.invitationCodesTabCodes')}
        </button>
        <button type="button" className={tabClass('usages')} onClick={() => setTab('usages')}>
          {t('admin.invitationCodesTabUsages')}
        </button>
      </div>

      {error ? (
        <p className="mt-4 text-sm text-red-400" role="alert">
          {error}
        </p>
      ) : null}

      {tab === 'codes' ? (
        <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
          <section className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
            <div className="mb-4 flex flex-wrap gap-3">
              <input
                type="text"
                value={codeFilter}
                onChange={(e) => setCodeFilter(e.target.value.toUpperCase())}
                placeholder={t('admin.invitationCodesFilterCode')}
                className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                aria-label={t('admin.invitationCodesFilterCode')}
              />
              <input
                type="text"
                value={remarkFilter}
                onChange={(e) => setRemarkFilter(e.target.value)}
                placeholder={t('admin.invitationCodesFilterRemark')}
                className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                aria-label={t('admin.invitationCodesFilterRemark')}
              />
              <select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as 'all' | 'active' | 'disabled')
                }
                className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                aria-label={t('admin.invitationCodesFilterStatus')}
              >
                <option value="all">{t('admin.invitationCodesStatusAll')}</option>
                <option value="active">{t('admin.invitationCodesStatusActive')}</option>
                <option value="disabled">{t('admin.invitationCodesStatusDisabled')}</option>
              </select>
              <button
                type="button"
                onClick={() => void loadCodes()}
                className="rounded-md bg-slate-800 px-3 py-2 text-sm text-white hover:bg-slate-700"
              >
                {t('admin.invitationCodesSearch')}
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-2 py-2">{t('admin.invitationCodesColCode')}</th>
                    <th className="px-2 py-2">{t('admin.invitationCodesColCodeType')}</th>
                    <th className="px-2 py-2">{t('admin.invitationCodesColLevel')}</th>
                    <th className="px-2 py-2">{t('admin.invitationCodesColDuration')}</th>
                    <th className="px-2 py-2">{t('admin.invitationCodesColRemark')}</th>
                    <th className="px-2 py-2">{t('admin.invitationCodesColReferrer')}</th>
                    <th className="px-2 py-2">{t('admin.invitationCodesColUses')}</th>
                    <th className="px-2 py-2">{t('admin.invitationCodesColDates')}</th>
                    <th className="px-2 py-2">{t('admin.invitationCodesColStatus')}</th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {codes.map((item) => (
                    <tr key={item.id} className="border-t border-slate-800 text-slate-200">
                      <td className="px-2 py-2 font-mono">{item.code}</td>
                      <td className="px-2 py-2 text-xs">{item.codeType}</td>
                      <td className="px-2 py-2">{item.level}</td>
                      <td className="px-2 py-2">
                        {item.levelDurationDays != null ? item.levelDurationDays : '-'}
                      </td>
                      <td
                        className="max-w-[12rem] truncate px-2 py-2 text-xs text-slate-400"
                        title={item.remark ?? undefined}
                      >
                        {item.remark ?? '-'}
                      </td>
                      <td
                        className="max-w-[10rem] truncate px-2 py-2 text-xs text-slate-400"
                        title={item.referrer?.email ?? undefined}
                      >
                        {item.referrer
                          ? `${item.referrer.nickname} (${item.referrer.email})`
                          : '—'}
                      </td>
                      <td className="px-2 py-2">
                        {item.usedCount}
                        {item.maxUses != null ? ` / ${item.maxUses}` : ` / ${t('admin.invitationCodesUnlimited')}`}
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-400">
                        {item.startDate ?? t('admin.invitationCodesImmediate')}
                        {' — '}
                        {item.endDate ?? t('admin.invitationCodesPermanent')}
                      </td>
                      <td className="px-2 py-2">{item.status}</td>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          className="text-amber-400 hover:underline"
                          onClick={() => handleEdit(item)}
                        >
                          {t('admin.invitationCodesEdit')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {codes.length === 0 ? (
                <p className="mt-4 text-sm text-slate-500">{t('admin.invitationCodesEmpty')}</p>
              ) : null}
            </div>
          </section>

          <section className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
            <h2 className="text-lg font-medium text-white">
              {editingId ? t('admin.invitationCodesEditTitle') : t('admin.invitationCodesCreateTitle')}
            </h2>
            <div className="mt-4 space-y-3">
              {!editingId ? (
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={form.autoGenerate}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, autoGenerate: e.target.checked }))
                    }
                  />
                  {t('admin.invitationCodesAutoGenerate')}
                </label>
              ) : null}
              {!form.autoGenerate ? (
                <div>
                  <label className="mb-1 block text-xs text-slate-400" htmlFor="ic-code">
                    {t('admin.invitationCodesColCode')}
                  </label>
                  <input
                    id="ic-code"
                    type="text"
                    maxLength={INVITATION_CODE_LENGTH}
                    value={form.code}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''),
                      }))
                    }
                    className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-sm uppercase"
                  />
                </div>
              ) : null}
              <div>
                <label className="mb-1 block text-xs text-slate-400" htmlFor="ic-code-type">
                  {t('admin.invitationCodesColCodeType')}
                </label>
                <select
                  id="ic-code-type"
                  value={form.codeType}
                  onChange={(e) => {
                    const nextType = e.target.value as InvitationCodeType;
                    setForm((prev) => ({
                      ...prev,
                      codeType: nextType,
                      level: resolveLevelForCodeType(nextType),
                    }));
                  }}
                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                >
                  <option value={INVITATION_CODE_TYPES.REGISTRATION_GRANT}>
                    {t('admin.invitationCodesTypeRegistrationGrant')}
                  </option>
                  <option value={INVITATION_CODE_TYPES.L1_TRIAL_EXTEND}>
                    {t('admin.invitationCodesTypeL1TrialExtend')}
                  </option>
                  <option value={INVITATION_CODE_TYPES.L3_TRIAL_PERIOD}>
                    {t('admin.invitationCodesTypeL3TrialPeriod')}
                  </option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-400" htmlFor="ic-level">
                  {t('admin.invitationCodesColLevel')}
                </label>
                <input
                  id="ic-level"
                  type="number"
                  min={1}
                  value={resolveLevelForCodeType(form.codeType)}
                  readOnly
                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm opacity-70"
                />
              </div>
              {form.codeType !== INVITATION_CODE_TYPES.REGISTRATION_GRANT ? (
                <div>
                  <label className="mb-1 block text-xs text-slate-400" htmlFor="ic-duration">
                    {t('admin.invitationCodesColDuration')}
                  </label>
                  <input
                    id="ic-duration"
                    type="number"
                    min={1}
                    value={form.levelDurationDays}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, levelDurationDays: e.target.value }))
                    }
                    className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                  />
                </div>
              ) : null}
              <div>
                <label className="mb-1 block text-xs text-slate-400" htmlFor="ic-remark">
                  {t('admin.invitationCodesColRemark')}
                </label>
                <textarea
                  id="ic-remark"
                  value={form.remark}
                  maxLength={500}
                  rows={3}
                  onChange={(e) => setForm((prev) => ({ ...prev, remark: e.target.value }))}
                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                  placeholder={t('admin.invitationCodesRemarkPlaceholder')}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-400" htmlFor="ic-referrer-email">
                  {t('admin.invitationCodesColReferrer')}
                </label>
                <p className="mb-2 text-xs text-slate-500">
                  {t('admin.invitationCodesReferrerHint')}
                </p>
                {form.referrerUserId ? (
                  <div className="mb-2 rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
                    <p>
                      {form.referrerNickname} · {form.referrerEmail}
                    </p>
                    <p className="text-xs text-slate-500">{form.referrerRole}</p>
                    <button
                      type="button"
                      onClick={handleClearReferrer}
                      className="mt-1 text-xs text-amber-400 hover:underline"
                    >
                      {t('admin.invitationCodesReferrerClear')}
                    </button>
                  </div>
                ) : null}
                <div className="flex gap-2">
                  <input
                    id="ic-referrer-email"
                    type="email"
                    value={referrerSearchEmail}
                    onChange={(e) => setReferrerSearchEmail(e.target.value)}
                    placeholder={t('admin.invitationCodesReferrerSearchPlaceholder')}
                    className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                    aria-label={t('admin.invitationCodesReferrerSearchPlaceholder')}
                  />
                  <button
                    type="button"
                    disabled={referrerSearching}
                    onClick={() => void handleSearchReferrer()}
                    className="shrink-0 rounded-md bg-slate-800 px-3 py-2 text-sm text-white hover:bg-slate-700 disabled:opacity-50"
                  >
                    {t('admin.invitationCodesSearch')}
                  </button>
                </div>
                {referrerSearchResults.length > 0 ? (
                  <ul className="mt-2 max-h-40 overflow-y-auto rounded-md border border-slate-700">
                    {referrerSearchResults.map((user) => (
                      <li key={user.id}>
                        <button
                          type="button"
                          onClick={() => handleSelectReferrer(user)}
                          className="w-full px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-800"
                        >
                          {user.nickname} · {user.email}
                          <span className="ml-2 text-xs text-slate-500">{user.role}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-400" htmlFor="ic-timezone">
                  {t('admin.invitationCodesTimezone')}
                </label>
                <input
                  id="ic-timezone"
                  type="text"
                  value={form.timezone}
                  onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))}
                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={form.immediateStart}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, immediateStart: e.target.checked }))
                  }
                />
                {t('admin.invitationCodesImmediate')}
              </label>
              {!form.immediateStart ? (
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setForm((prev) => ({ ...prev, startDate: e.target.value }))}
                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                  aria-label={t('admin.invitationCodesStartDate')}
                />
              ) : null}
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={form.permanentEnd}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, permanentEnd: e.target.checked }))
                  }
                />
                {t('admin.invitationCodesPermanent')}
              </label>
              {!form.permanentEnd ? (
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm((prev) => ({ ...prev, endDate: e.target.value }))}
                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                  aria-label={t('admin.invitationCodesEndDate')}
                />
              ) : null}
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={form.unlimitedMaxUses}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, unlimitedMaxUses: e.target.checked }))
                  }
                />
                {t('admin.invitationCodesUnlimited')}
              </label>
              {!form.unlimitedMaxUses ? (
                <input
                  type="number"
                  min={1}
                  value={form.maxUses}
                  onChange={(e) => setForm((prev) => ({ ...prev, maxUses: e.target.value }))}
                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                  aria-label={t('admin.invitationCodesMaxUses')}
                />
              ) : null}
              {editingId ? (
                <div>
                  <label className="mb-1 block text-xs text-slate-400" htmlFor="ic-status">
                    {t('admin.invitationCodesColStatus')}
                  </label>
                  <select
                    id="ic-status"
                    value={form.status}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        status: e.target.value as CodeFormState['status'],
                      }))
                    }
                    className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                  >
                    <option value={INVITATION_CODE_STATUS.ACTIVE}>
                      {t('admin.invitationCodesStatusActive')}
                    </option>
                    <option value={INVITATION_CODE_STATUS.DISABLED}>
                      {t('admin.invitationCodesStatusDisabled')}
                    </option>
                  </select>
                </div>
              ) : null}
            </div>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => void handleSubmit()}
                className="rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50"
              >
                {saving ? t('admin.saving') : t('admin.invitationCodesSave')}
              </button>
              {editingId ? (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="rounded-md bg-slate-800 px-4 py-2 text-sm text-slate-200 hover:bg-slate-700"
                >
                  {t('admin.invitationCodesCancel')}
                </button>
              ) : null}
            </div>
          </section>
        </div>
      ) : (
        <section className="mt-6 rounded-lg border border-slate-800 bg-slate-900/60 p-4">
          <div className="mb-4 flex flex-wrap gap-3">
            <input
              type="text"
              value={usageCodeFilter}
              onChange={(e) => setUsageCodeFilter(e.target.value.toUpperCase())}
              placeholder={t('admin.invitationCodesFilterCode')}
              className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              aria-label={t('admin.invitationCodesFilterCode')}
            />
            <input
              type="date"
              value={usageStartDate}
              onChange={(e) => setUsageStartDate(e.target.value)}
              className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              aria-label={t('admin.invitationCodesUsageStart')}
            />
            <input
              type="date"
              value={usageEndDate}
              onChange={(e) => setUsageEndDate(e.target.value)}
              className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              aria-label={t('admin.invitationCodesUsageEnd')}
            />
            <button
              type="button"
              onClick={() => void loadUsages()}
              className="rounded-md bg-slate-800 px-3 py-2 text-sm text-white hover:bg-slate-700"
            >
              {t('admin.invitationCodesSearch')}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-2 py-2">{t('admin.invitationCodesColCode')}</th>
                  <th className="px-2 py-2">{t('admin.invitationCodesColEmail')}</th>
                  <th className="px-2 py-2">{t('admin.invitationCodesColNickname')}</th>
                  <th className="px-2 py-2">{t('admin.invitationCodesColLevelGranted')}</th>
                  <th className="px-2 py-2">{t('admin.invitationCodesColCurrentLevel')}</th>
                  <th className="px-2 py-2">{t('admin.invitationCodesColFirstPayment')}</th>
                  <th className="px-2 py-2">{t('admin.invitationCodesColUsageContext')}</th>
                  <th className="px-2 py-2">{t('admin.invitationCodesColUsedAt')}</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {usages.map((row) => (
                  <tr key={row.id} className="border-t border-slate-800 text-slate-200">
                    <td className="px-2 py-2 font-mono">{row.invitationCode}</td>
                    <td className="px-2 py-2">{row.userEmail}</td>
                    <td className="px-2 py-2">{row.userNickname}</td>
                    <td className="px-2 py-2">{row.userLevelGranted}</td>
                    <td className="px-2 py-2">
                      {row.currentUserLevel != null ? row.currentUserLevel : '—'}
                    </td>
                    <td className="px-2 py-2 text-xs">
                      {row.firstPaymentAt ?? '—'}
                    </td>
                    <td className="px-2 py-2">{row.usageContext}</td>
                    <td className="px-2 py-2">{row.usedAt}</td>
                    <td className="px-2 py-2">
                      {row.userId ? (
                        <Link
                          to={adminPortalPath(`users/${row.userId}`)}
                          className="text-amber-400 hover:underline"
                        >
                          {t('admin.invitationCodesDetail')}
                        </Link>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {usages.length === 0 ? (
              <p className="mt-4 text-sm text-slate-500">{t('admin.invitationCodesUsagesEmpty')}</p>
            ) : null}
          </div>
        </section>
      )}
    </div>
  );
};
